import React, { useMemo } from 'react';

import CardDispatcher from '~/src/common/components/ProductsGrid/CardDispatcher';
import { useSearchParams } from '~/src/common/components/Search/hooks';
import I18n from '~/src/common/services/I18n';
import { CardInfoWithoutPositionAndCountProperties } from '~/src/common/services/Tracker';
import { AddProductBodyProductAddToCartSource } from '~/src/queries/api-ecom/generated/api-ecom.schemas';
import { ProductPlaceholder } from '~/src/screens/CatalogContent/ListProductsScreen/layout';
import { isProduct } from '~/src/typings/products/guards';
import { CatalogItem } from '~/src/typings/products/types';

import { EmptyText, Grid } from './layout';

type Props = {
  products?: CatalogItem[];
  placeholders?: string[];
  gridComponent?: React.ComponentType;
  eventSource: AddProductBodyProductAddToCartSource;
  categoryName?: string;
  subcategory?: { id: string; name: string };
  onRefresh?: () => Promise<unknown>;
  onCardClick?: (item: CatalogItem) => void;
  categoryAnalyticsProperties?: CardInfoWithoutPositionAndCountProperties;
};

const VISIBLE_CLASS = 'product-card';

const ProductsGrid = ({
  products,
  placeholders,
  gridComponent,
  eventSource,
  categoryName,
  subcategory,
  onRefresh,
  onCardClick,
  categoryAnalyticsProperties,
}: Props) => {
  const { searchID } = useSearchParams();
  const GridComponent = gridComponent ?? Grid;

  const items = useMemo(() => {
    return (placeholders ?? products?.map(p => p.id) ?? []).map(productId => {
      const product = products?.find(p => p.id === productId);
      return { product, productId };
    });
  }, [placeholders, products]);

  const numberOfProducts = items.filter(i => i.product && isProduct(i.product)).length;
  const numberOfCards = items.filter(i => !!i.product).length;

  return items.length === 0 ? (
    <EmptyText>{I18n.t('products-grid.empty')}</EmptyText>
  ) : (
    <GridComponent>
      {items.map(({ product, productId }) =>
        product ? (
          <CardDispatcher
            key={productId}
            product={product}
            eventSource={eventSource}
            categoryName={categoryName}
            subcategory={subcategory}
            onRefresh={onRefresh}
            onClick={onCardClick}
            analyticsProperties={{
              'number of products in the list': numberOfProducts,
              'number of cards in the list': numberOfCards,
              ...(searchID ? { 'search ID': searchID } : null),
              ...categoryAnalyticsProperties,
            }}
          />
        ) : (
          <ProductPlaceholder className={VISIBLE_CLASS} id={productId} key={productId} />
        ),
      )}
    </GridComponent>
  );
};

export default React.memo(ProductsGrid);
